/**
 * AuthProvider Component (Basic)
 * 
 * Handles JWT authentication only:
 * 1. Check for OAuth redirect callback
 * 2. Verify user is authenticated (JWT exists)
 * 3. Load app or redirect to login
 * 
 * This does NOT check app-specific permissions.
 * Use this if your app doesn't need DynamoDB permission checks.
 */

import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import { getCurrentPath, navigateTo } from '../utils/navigation';
import { consumeAfterLogin } from '../utils/afterLogin';
import { isTransientNetworkError, subscribeSessionWakeRefresh, waitForRecoverableSession } from '../utils/auth';
import {
  resolveFokusRoleFromIdToken,
  WorkspaceAPI,
  setActiveWorkspaceId,
  hydrateWorkspaceIdFromStorage,
  getStoredWorkspacePreference,
  workspaceRoleToDocsRole,
  PERSONAL_WORKSPACE_ID,
} from '../utils/api';
import { isWorkspaceCovered, normalizeWorkspaceRole } from '../utils/workspaceAccess';
import { decorateWorkspaces } from '../utils/workspaceName';

// Create Auth Context
const AuthContext = createContext(null);

function pickActiveWorkspace(workspaces, storedPref) {
  const named = workspaces.filter(
    (w) => w.id && w.id !== PERSONAL_WORKSPACE_ID
  );
  const covered = named.filter(isWorkspaceCovered);
  const home = named.find((w) => w.home);
  const preferred = workspaces.find((w) => w.id === storedPref);
  if (preferred && preferred.id !== PERSONAL_WORKSPACE_ID) {
    if (isWorkspaceCovered(preferred)) return preferred;
    // Just joined this room (invite) — open it even if it still needs a plan.
    if (normalizeWorkspaceRole(preferred.role) !== 'owner') return preferred;
    // Last opened was a room they own that needs a plan. Prefer a covered room
    // so they do not land on the plan screen and miss the invited room.
    if (covered[0]) return covered[0];
    return preferred;
  }
  if (home && isWorkspaceCovered(home)) return home;
  if (covered[0]) return covered[0];
  if (named[0]) return named[0];
  return preferred || workspaces[0] || null;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Note: Loading screen removed - App.js handles the unified loading overlay
// to prevent multiple loader animations from "glitching" during startup

// AuthProvider Component
export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    loading: true,
    authenticated: false,
    user: null,
    role: null,
    workspaceId: null,
    workspaces: [],
    pendingInvites: [],
  });
  
  const initRef = useRef(false);
  const isInitializing = useRef(false);

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (initRef.current) return;
    initRef.current = true;
    
    if (!isInitializing.current) {
      isInitializing.current = true;
      initAuth();
    }
  }, []);

  useEffect(() => subscribeSessionWakeRefresh(), []);

  const waitForScripts = async () => {
    let attempts = 0;
    const maxAttempts = 50;
    
    while (attempts < maxAttempts) {
      if (window.Auth && window.CONFIG) {
        return true;
      }
      
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    console.error('❌ Auth system failed to load after waiting');
    return false;
  };

  const initAuth = async () => {
    try {
      // Wait for shared scripts to load
      const scriptsLoaded = await waitForScripts();
      if (!scriptsLoaded) {
        throw new Error('Auth system failed to load');
      }

      // Step 1: Restore session (Electron OS-encrypted store + refresh if access token expired)
      // Step 1b: OAuth redirect — run after cold-start restore so PKCE exchange is not overwritten
      const hadCode = new URLSearchParams(window.location.search).has('code');

      if (!hadCode) {
        await window.Auth.ensureSessionReady?.();
      }
      if (hadCode) {
        await window.Auth.handleRedirectIfPresent();
      }
      if (hadCode && !window.Auth.isAuthenticated()) {
        await window.Auth.ensureSessionReady?.();
      }

      // Step 2: Check if authenticated.
      // After sleep the access token is often expired and the first refresh fails
      // because DNS isn't back yet. Wait for the retry loop instead of bouncing
      // to login — that reload is what left the Fokus loader on screen.
      if (!window.Auth.isAuthenticated() && localStorage.getItem('refresh_token')) {
        await waitForRecoverableSession();
      }
      const isAuth = window.Auth.isAuthenticated();
      
      if (!isAuth) {
        if (window.electronAPI?.isElectron) {
          navigateTo('/auth/login');
          return;
        }
        const path = getCurrentPath() || '/';
        if (path !== '/') {
          navigateTo('/');
          return;
        }
        setAuthState({
          loading: false,
          authenticated: false,
          user: null,
          role: null,
          workspaceId: null,
          workspaces: [],
          pendingInvites: [],
        });
        return;
      }

      // Step 3: Get user info
      const user = window.Auth.getUser();
      if (!user || !user.sub) {
        console.error('❌ No user info found');
        navigateTo('/auth/login');
        return;
      }

      // Step 4: Start auto token refresh
      window.Auth.startAutoRefresh();

      // Step 5: Docs role (default admin; optional Cognito custom:fokusRole | fokus_role | custom:role)
      const idToken = window.Auth.getIdToken?.();
      let docsRole = resolveFokusRoleFromIdToken(idToken);
      let workspaceId = null;
      let workspaces = [];
      let pendingInvites = [];

      hydrateWorkspaceIdFromStorage();
      let wsRes = null;
      for (;;) {
        try {
          wsRes = await WorkspaceAPI.list();
          break;
        } catch (wsErr) {
          if (!isTransientNetworkError(wsErr) || !localStorage.getItem('refresh_token')) {
            console.warn('Workspace list unavailable:', wsErr);
            wsRes = null;
            break;
          }
          console.warn('Workspace list failed while the network was down, retrying:', wsErr);
          await new Promise((resolve) => setTimeout(resolve, 1500));
          if (!window.Auth.isAuthenticated()) {
            const recovered = await waitForRecoverableSession();
            if (!recovered) {
              wsRes = null;
              break;
            }
          }
        }
      }
      if (wsRes) {
        workspaces = decorateWorkspaces(wsRes.workspaces || [], user);
        pendingInvites = Array.isArray(wsRes.pendingInvites) ? wsRes.pendingInvites : [];
        const storedPref = getStoredWorkspacePreference();
        const activeWs = pickActiveWorkspace(workspaces, storedPref);
        if (activeWs) {
          setActiveWorkspaceId(activeWs.id);
          workspaceId = activeWs.id;
          docsRole = workspaceRoleToDocsRole(activeWs.role);
        } else {
          setActiveWorkspaceId(null);
          workspaceId = null;
        }
      } else {
        setActiveWorkspaceId(null);
        workspaceId = null;
      }

      const afterLogin = consumeAfterLogin();
      if (afterLogin) {
        navigateTo(afterLogin);
        return;
      }

      setAuthState({
        loading: false,
        authenticated: true,
        user,
        role: docsRole,
        workspaceId,
        workspaces,
        pendingInvites,
      });

    } catch (error) {
      console.error('❌ Auth initialization failed:', error);
      
      if (window.Auth && !window.Auth.isAuthenticated()) {
        if (window.electronAPI?.isElectron) {
          navigateTo('/auth/login');
        } else if ((getCurrentPath() || '/') !== '/') {
          navigateTo('/');
        }
      }
      
      setAuthState({
        loading: false,
        authenticated: false,
        user: null,
        role: null,
        workspaceId: null,
        workspaces: [],
        pendingInvites: [],
      });
    }
  };

  const setWorkspace = useCallback((nextWorkspaceId) => {
    setAuthState((prev) => {
      const ws = prev.workspaces.find((w) => w.id === nextWorkspaceId);
      if (!ws) return prev;
      setActiveWorkspaceId(ws.id);
      return {
        ...prev,
        workspaceId: ws.id,
        role: workspaceRoleToDocsRole(ws.role),
      };
    });
  }, []);

  const refreshPendingInvites = useCallback(async () => {
    try {
      const wsRes = await WorkspaceAPI.listPendingInvites();
      const pendingInvites = Array.isArray(wsRes.pendingInvites) ? wsRes.pendingInvites : [];
      setAuthState((prev) => {
        if (!prev.authenticated) return prev;
        return { ...prev, pendingInvites };
      });
    } catch (wsErr) {
      console.warn('Invite inbox refresh failed:', wsErr);
    }
  }, []);

  const refreshWorkspaces = useCallback(async () => {
    const idToken = window.Auth?.getIdToken?.();
    const jwtFallback = resolveFokusRoleFromIdToken(idToken);
    try {
      const wsRes = await WorkspaceAPI.list();
      const workspaces = decorateWorkspaces(wsRes.workspaces || [], window.Auth?.getUser?.());
      const pendingInvites = Array.isArray(wsRes.pendingInvites) ? wsRes.pendingInvites : [];
      setAuthState((prev) => {
        if (!prev.authenticated) return prev;
        const storedPref = getStoredWorkspacePreference();
        const activeWs = pickActiveWorkspace(workspaces, storedPref);
        if (activeWs) {
          setActiveWorkspaceId(activeWs.id);
          return {
            ...prev,
            workspaces,
            pendingInvites,
            workspaceId: activeWs.id,
            role: workspaceRoleToDocsRole(activeWs.role),
          };
        }
        setActiveWorkspaceId(null);
        return {
          ...prev,
          workspaces,
          pendingInvites,
          workspaceId: null,
          role: jwtFallback,
        };
      });
    } catch (wsErr) {
      console.warn('Workspace list refresh failed:', wsErr);
    }
  }, []);

  const renameWorkspace = useCallback((workspaceId, name) => {
    const trimmed = (name || '').trim();
    if (!workspaceId || !trimmed) return;
    setAuthState((prev) => ({
      ...prev,
      workspaces: (prev.workspaces || []).map((w) =>
        w.id === workspaceId ? { ...w, name: trimmed } : w
      ),
    }));
  }, []);

  const logout = async () => {
    setActiveWorkspaceId(null);
    if (window.Auth?.logout) {
      await window.Auth.logout();
    }
  };

  // Don't show loading screen here - App.js has a unified loading overlay
  // This prevents the loading animation from "glitching" (restarting) during startup

  // Render app with auth context
  return (
    <AuthContext.Provider value={{ ...authState, logout, setWorkspace, refreshWorkspaces, refreshPendingInvites, renameWorkspace }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
